import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Text, Button, Group, TextInput, Select, Modal, Stack, Table, ActionIcon, Tooltip, Badge, Center, Loader, Textarea, Image, SimpleGrid, Checkbox, ScrollArea, SegmentedControl, Switch, Divider, Card, Menu } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useBreakpoint } from '../../hooks/useBreakpoint'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import { IconPlus, IconEdit, IconTrash, IconSearch, IconServer, IconMapPin, IconPhoto, IconDotsVertical } from '@tabler/icons-react'
import { getComsets, createComset, updateComset, deleteComset, getCheckTemplates, getLocations, getAssets, getModels, getDepartments } from '../../api/client'
import SmartDateInput from '../SmartDateInput'

export default function ComsetTab() {
  const { t } = useTranslation()
  const { isMobile } = useBreakpoint()
  const [comsets, setComsets] = useState<any[]>([])
  const [templates, setTemplates] = useState<any[]>([])
  const [locations, setLocations] = useState<any[]>([])
  const [assets, setAssets] = useState<any[]>([])
  const [deptOptions, setDeptOptions] = useState<{ value: string; label: string }[]>([])
  const [loading, setLoading] = useState(true)
  const [modalLoading, setModalLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [assetSearch, setAssetSearch] = useState('')
  const [opened, { open, close }] = useDisclosure(false)
  const [editing, setEditing] = useState<any>(null)
  const [saving, setSaving] = useState(false)
  const todayISO = () => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }

  const [form, setForm] = useState({
    name: '', description: '', location: '', departmentCode: '',
    assetIds: [] as number[], templateId: null as number | null,
    checkFrequency: 'MONTHLY', checkStartDate: todayISO(),
    skipSnCheck: false, checklistMode: 'per-device',
  })

  const loadComsets = async () => {
    setLoading(true)
    try { const r = await getComsets(); setComsets(r.data ?? []) }
    catch {} finally { setLoading(false) }
  }

  const loadModalData = async () => {
    setModalLoading(true)
    try {
      const [t, l, a, m, d] = await Promise.all([getCheckTemplates(), getLocations(), getAssets(), getModels(), getDepartments()])
      setTemplates(t.data ?? [])
      setLocations((l.data ?? []).filter((x: any) => x.isActive !== false))
      setDeptOptions((d.data ?? []).map((dep: any) => ({ value: dep.code, label: dep.name ?? dep.code })))
      // Map model imageUrl into assets
      const modelImageMap: Record<string, string> = {}
      ;(m.data ?? []).forEach((mod: any) => { if (mod.code && mod.imageUrl) modelImageMap[mod.code] = mod.imageUrl })
      setAssets((a.data ?? []).map((asset: any) => ({
        ...asset,
        imageUrl: asset.imageUrl || (asset.model ? modelImageMap[asset.model] : undefined),
      })))
    } catch {} finally { setModalLoading(false) }
  }

  useEffect(() => { loadComsets() }, [])

  const openCreate = async () => {
    setEditing(null)
    setForm({ name: '', description: '', location: '', departmentCode: '', assetIds: [], templateId: null, checkFrequency: 'MONTHLY', checkStartDate: todayISO(), skipSnCheck: false, checklistMode: 'per-device' })
    setAssetSearch('')
    open()
    await loadModalData()
  }

  const openEdit = async (c: any) => {
    setEditing(c)
    setForm({ name: c.name, description: c.description ?? '', location: c.location ?? '', departmentCode: c.departmentCode ?? '', assetIds: c.assetIds ?? [], templateId: c.templateId ?? null, checkFrequency: c.checkFrequency ?? 'MONTHLY', checkStartDate: c.checkStartDate ?? todayISO(), skipSnCheck: c.skipSnCheck ?? false, checklistMode: c.checklistMode ?? 'grouped' })
    setAssetSearch('')
    open()
    await loadModalData()
  }

  const toggleAsset = (id: number) => {
    setForm(p => ({
      ...p,
      assetIds: p.assetIds.includes(id) ? p.assetIds.filter(x => x !== id) : [...p.assetIds, id]
    }))
  }

  const save = async () => {
    if (!form.name.trim()) { notifications.show({ color: 'red', message: 'Please enter a COMSET name' }); return }
    setSaving(true)
    try {
      const payload = { ...form, templateId: form.templateId || undefined }
      editing ? await updateComset(editing.id, payload) : await createComset(payload)
      notifications.show({ color: 'teal', message: 'Saved successfully' })
      close(); loadComsets()
    } catch { notifications.show({ color: 'red', message: 'An error occurred' }) }
    finally { setSaving(false) }
  }

  const handleDelete = (c: any) => modals.openConfirmModal({
    title: 'Delete COMSET', children: <Text size="sm">Confirm delete <b>{c.name}</b>? Check history will also be deleted</Text>,
    labels: { confirm: t('comset.delete','Delete'), cancel: t('comset.cancel','Cancel') }, confirmProps: { color: 'red' },
    onConfirm: async () => { await deleteComset(c.id); loadComsets() },
  })

  const getDueStatus = (c: any) => {
    if (!c.nextCheckDue) return { color: 'gray', label: 'Not set' }
    const days = Math.ceil((new Date(c.nextCheckDue).getTime() - Date.now()) / 86400000)
    if (days < 0)  return { color: 'red',    label: `Overdue ${Math.abs(days)} days` }
    if (days <= 7) return { color: 'orange',  label: `${days} days left` }
    return { color: 'teal', label: `${days} days left` }
  }

  const filtered = comsets.filter(c => !search || c.name?.toLowerCase().includes(search.toLowerCase()) || c.location?.toLowerCase().includes(search.toLowerCase()))

  const filteredAssets = assets.filter(a =>
    !assetSearch ||
    a.assetCode?.toLowerCase().includes(assetSearch.toLowerCase()) ||
    a.name?.toLowerCase().includes(assetSearch.toLowerCase()) ||
    a.serialNumber?.toLowerCase().includes(assetSearch.toLowerCase()) ||
    a.model?.toLowerCase().includes(assetSearch.toLowerCase()) ||
    a.departmentCode?.toLowerCase().includes(assetSearch.toLowerCase())
  )

  const selectedAssets = assets.filter(a => form.assetIds.includes(a.id))

  return (
    <Box>
      <Group justify="space-between" mb="md" wrap="wrap">
        <Group gap={8}><IconServer size={18} color="#10b981" /><Text fw={600}>COMSET <Badge color="teal" variant="light" size="sm">{comsets.length}</Badge></Text></Group>
        <Group gap={8} wrap="wrap">
          <TextInput size="xs" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} leftSection={<IconSearch size={13} />} style={{ width: isMobile ? '100%' : 200 }} />
          <Button size="xs" leftSection={<IconPlus size={13} />} color="teal" onClick={openCreate}>{t('common.create', 'Create')} COMSET</Button>
        </Group>
      </Group>

      <Box style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
        {loading ? <Center p="xl"><Loader size="sm" color="teal" /></Center> : isMobile ? (
          filtered.length === 0 ? (
            <Center p="xl"><Text size="sm" c="dimmed">No COMSET yet</Text></Center>
          ) : (
            <Stack gap="xs" p="xs">
              {filtered.map(c => {
                const due = getDueStatus(c)
                const tpl = templates.find(t => t.id === c.templateId)
                return (
                  <Card key={c.id} withBorder radius="lg" p="md">
                    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
                      <Box style={{ minWidth: 0, flex: 1 }}>
                        <Text size="sm" fw={600}>{c.name}</Text>
                        {c.description && <Text size="xs" c="dimmed" lineClamp={1}>{c.description}</Text>}
                        <Group gap={4} mt={2}>
                          <IconMapPin size={12} color="#94a3b8" />
                          <Text size="xs" c="dimmed">{c.location || '-'}</Text>
                          {c.departmentCode && <Badge size="xs" variant="light" color="blue">{c.departmentCode}</Badge>}
                        </Group>
                        <Group gap={6} mt={4}>
                          <Badge color="blue" variant="light" size="xs">{(c.assetIds ?? []).length} item(s)</Badge>
                          {tpl && <Text size="xs" c="dimmed">{tpl.name}</Text>}
                        </Group>
                      </Box>
                      <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
                        <Badge color={due.color} variant="light" size="xs">{due.label}</Badge>
                        <Menu shadow="md" width={160} position="bottom-end">
                          <Menu.Target>
                            <ActionIcon variant="subtle" color="gray" size="lg"><IconDotsVertical size={18} /></ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item leftSection={<IconEdit size={15} />} onClick={() => openEdit(c)}>{t('common.edit', 'Edit')}</Menu.Item>
                            <Menu.Item leftSection={<IconTrash size={15} />} color="red" onClick={() => handleDelete(c)}>{t('common.delete', 'Delete')}</Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Group>
                    </Group>
                    <Text size="xs" c="dimmed" mt={6}>
                      {c.lastCheckedAt ? `${t('common.lastChecked', 'Last checked')}: ${new Date(c.lastCheckedAt).toLocaleDateString('en-GB')}` : t('common.neverChecked', 'Never checked')}
                    </Text>
                  </Card>
                )
              })}
            </Stack>
          )
        ) : (
          <Table highlightOnHover>
            <Table.Thead style={{ background: '#f8fafc' }}>
              <Table.Tr>
                {['Name', 'Location', 'Assets', 'Template', 'Last Checked', 'Next Due', ''].map(h => (
                  <Table.Th key={h} style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase' }}>{h}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.length === 0
                ? <Table.Tr><Table.Td colSpan={7}><Center p="xl"><Text size="sm" c="dimmed">No COMSET yet</Text></Center></Table.Td></Table.Tr>
                : filtered.map(c => {
                  const due = getDueStatus(c)
                  const tpl = templates.find(t => t.id === c.templateId)
                  return (
                    <Table.Tr key={c.id}>
                      <Table.Td><Text size="sm" fw={600}>{c.name}</Text>{c.description && <Text size="xs" c="dimmed" lineClamp={1}>{c.description}</Text>}</Table.Td>
                      <Table.Td><Group gap={4}><IconMapPin size={12} color="#94a3b8" /><Text size="sm">{c.location || '-'}</Text>{c.departmentCode && <Badge size="xs" variant="light" color="blue">{c.departmentCode}</Badge>}</Group></Table.Td>
                      <Table.Td><Badge color="blue" variant="light" size="sm">{(c.assetIds ?? []).length} item(s)</Badge></Table.Td>
                      <Table.Td><Text size="xs" c="dimmed">{tpl?.name || '-'}</Text></Table.Td>
                      <Table.Td><Text size="xs" c="dimmed">{c.lastCheckedAt ? new Date(c.lastCheckedAt).toLocaleDateString('en-GB') : 'Never'}</Text></Table.Td>
                      <Table.Td>
                        <Tooltip label={c.checkFrequency ? `Checked ${c.checkFrequency.toLowerCase()}` : undefined} disabled={!c.checkFrequency}>
                          <Badge color={due.color} variant="light" size="xs">{due.label}</Badge>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4} justify="flex-end">
                          <Tooltip label={t('common.edit', 'Edit')}><ActionIcon size="sm" variant="subtle" color="blue" onClick={() => openEdit(c)}><IconEdit size={14} /></ActionIcon></Tooltip>
                          <Tooltip label={t('common.delete', 'Delete')}><ActionIcon size="sm" variant="subtle" color="red" onClick={() => handleDelete(c)}><IconTrash size={14} /></ActionIcon></Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
            </Table.Tbody>
          </Table>
        )}
      </Box>

      <Modal opened={opened} onClose={close} title={<Text fw={600}>{editing ? t('comset.editTitle','Edit COMSET') : t('comset.createTitle','Create COMSET')}</Text>} size="xl" radius="lg" fullScreen={isMobile}>
        <Stack gap="sm">
          <TextInput label={t("comset.name","COMSET Name *")} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder={t("comset.namePlaceholder","e.g. Front Desk Station 1")} />
          <Textarea label={t("comset.description","Description")} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} minRows={2} />

          <Group grow wrap="wrap">
            <Select label={t("comset.location","Location *")} data={locations.map((l: any) => ({ value: l.name, label: l.name }))}
              value={form.location || null} onChange={v => setForm(p => ({ ...p, location: v ?? '' }))}
              searchable clearable placeholder={t("comset.locationPlaceholder","Select Location")} />
            <Select label={t("comset.department","Department")} data={deptOptions}
              value={form.departmentCode || null} onChange={v => setForm(p => ({ ...p, departmentCode: v ?? '' }))}
              searchable clearable placeholder={t("comset.deptPlaceholder","Select Department (optional)")} />
          </Group>

          <Group grow align="flex-start" wrap="wrap">
            <Select
              label={t("comset.checkFrequency","Check Frequency")}
              data={[
                { value: 'DAILY',     label: 'Daily' },
                { value: 'WEEKLY',    label: 'Weekly' },
                { value: 'MONTHLY',   label: 'Monthly' },
                { value: 'QUARTERLY', label: 'Quarterly' },
                { value: 'YEARLY',    label: 'Yearly' },
              ]}
              value={form.checkFrequency}
              onChange={v => setForm(p => ({ ...p, checkFrequency: v ?? 'MONTHLY' }))}
              allowDeselect={false}
            />
            <SmartDateInput
              label={t("comset.startDate","Start Date")}
              description="When the checking schedule begins"
              value={form.checkStartDate}
              onChange={v => setForm(p => ({ ...p, checkStartDate: v }))}
            />
          </Group>

          {/* ── Check Behavior Settings ── */}
          <Box style={{ background: '#f0fdf4', borderRadius: 10, padding: '12px 14px', border: '1px solid #bbf7d0' }}>
            <Text size="xs" fw={700} c="teal" mb={10} tt="uppercase" style={{ letterSpacing: '0.5px' }}>
              ⚙️ Check Behavior Settings
            </Text>
            <Stack gap={10}>
              <Switch
                label={<Text size="sm" fw={500}>Skip Serial Number Check</Text>}
                description="When enabled, SN is not counted toward Pass/Fail every time this COMSET is checked"
                checked={form.skipSnCheck}
                onChange={e => setForm(p => ({ ...p, skipSnCheck: e.currentTarget.checked }))}
                color="orange"
              />
              <Divider />
              <Box>
                <Text size="sm" fw={500} mb={6}>Maintenance Checklist Mode</Text>
                <SegmentedControl
                  size="xs"
                  orientation={isMobile ? 'vertical' : 'horizontal'}
                  value={form.checklistMode}
                  onChange={v => setForm(p => ({ ...p, checklistMode: v }))}
                  data={[
                    { value: 'grouped', label: '✅ Grouped — tick once applies to all devices' },
                    { value: 'per-device', label: '📋 Per Device — tick separately for each item' },
                  ]}
                />
                <Text size="xs" c="dimmed" mt={4}>
                  {form.checklistMode === 'grouped'
                    ? 'Best for COMSETs with many devices that share the same checklist'
                    : 'Best for tracking checklist per individual item, e.g. cleaning each one separately'}
                </Text>
              </Box>
            </Stack>
          </Box>

          <Select label={t("comset.template","Check Template")}
            data={templates.map(t => ({ value: String(t.id), label: t.name }))}
            value={form.templateId ? String(form.templateId) : null}
            onChange={v => setForm(p => ({ ...p, templateId: v ? Number(v) : null }))}
            searchable clearable placeholder={modalLoading ? 'Loading...' : 'Select Template (optional)'}
            disabled={modalLoading} />

          {/* Asset selector — card grid style like Handover */}
          <Box>
            <Text size="sm" fw={500} mb={4}>
              {t('comset.assets','Assets in COMSET')}
              {form.assetIds.length > 0 && <Badge color="teal" variant="light" size="sm" ml={8}>{form.assetIds.length} selected</Badge>}
            </Text>

            {/* Selected chips */}
            {selectedAssets.length > 0 && (
              <Group gap={6} mb={8} wrap="wrap">
                {selectedAssets.map(a => (
                  <Badge key={a.id} color="teal" variant="light" size="sm"
                    rightSection={<Text size="xs" style={{ cursor: 'pointer' }} onClick={() => toggleAsset(a.id)}>✕</Text>}>
                    {a.assetCode}
                  </Badge>
                ))}
              </Group>
            )}

            <TextInput size="xs" placeholder="Search code, name, S/N, model, dept..." value={assetSearch}
              onChange={e => setAssetSearch(e.target.value)}
              leftSection={<IconSearch size={13} />} mb={8} />

            {modalLoading ? <Center p="xl"><Loader size="sm" color="teal" /></Center> : (
              <ScrollArea h={280} style={{ border: '1px solid #e2e8f0', borderRadius: 8 }}>
                <SimpleGrid cols={isMobile ? 1 : 2} spacing="xs" p="xs">
                  {filteredAssets.map(a => {
                    const selected = form.assetIds.includes(a.id)
                    return (
                      <Box key={a.id} onClick={() => toggleAsset(a.id)}
                        style={{
                          display: 'flex', gap: 10, alignItems: 'center',
                          padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                          border: selected ? '1.5px solid #14b8a6' : '1px solid #e2e8f0',
                          background: selected ? '#f0fdf4' : '#fafafa',
                          transition: 'all .15s',
                        }}>
                        {a.imageUrl ? (
                          <Image src={a.imageUrl} w={36} h={36} radius="sm" fit="cover" style={{ flexShrink: 0 }} />
                        ) : (
                          <Box w={36} h={36} style={{ background: '#eee', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <IconPhoto size={16} color="#bbb" />
                          </Box>
                        )}
                        <Box style={{ minWidth: 0 }}>
                          <Text size="xs" fw={700} style={{ color: '#0f172a' }} lineClamp={1}>{a.assetCode}</Text>
                          <Text size="xs" c="dimmed" lineClamp={1}>{a.name}</Text>
                          {a.serialNumber && <Text size="xs" style={{ color: '#94a3b8', fontFamily: 'monospace' }} lineClamp={1}>{a.serialNumber}</Text>}
                        </Box>
                        {selected && <Checkbox size="xs" checked readOnly style={{ marginLeft: 'auto', flexShrink: 0 }} />}
                      </Box>
                    )
                  })}
                  {filteredAssets.length === 0 && (
                    <Box style={{ gridColumn: '1 / -1' }}>
                      <Center p="xl"><Text size="sm" c="dimmed">No assets found</Text></Center>
                    </Box>
                  )}
                </SimpleGrid>
              </ScrollArea>
            )}
          </Box>

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={close}>{t('common.cancel', 'Cancel')}</Button>
            <Button color="teal" loading={saving} onClick={save}>{editing ? t('comset.save','Save') : t('comset.create','Create')}</Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  )
}
